import { FC, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { SubmissionCommentDisplay } from "@app/compliance-connect/network-submission/interfaces";
import { NetworkProfileCommentSection } from "@app/compliance-connect/issuer/network-profile/NetworkProfileCommentSection";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import LoadingSpinner from "@app/shared/LoadingSpinner";

interface NetworkProfileCommentLogProps {
  comments: SubmissionCommentDisplay[];
  isError: boolean;
  isLoading: boolean;
  submissionId: number;
}

export const NetworkProfileCommentLog: FC<NetworkProfileCommentLogProps> = ({ comments, isError, isLoading, submissionId }) => {
  const service = useRef(new NetworkSubmissionService()).current;
  const queryClient = useQueryClient();

  const handleCommentViewed = (submissionCommentId: number): void => {
    service.markSubmissionCommentAsRead(submissionId, submissionCommentId)
      .then(() => queryClient.invalidateQueries({ queryKey: ["ns-submission-comments", submissionId] }))
      .catch(() => {
        toast(<span>Failed to mark comment as read.</span>, { containerId: "z-index-9999", type: "error" });
      });
  };

  const newComments = comments.filter(comment => !comment.isRead);
  const history = comments.filter(comment => comment.isRead);

  if (isLoading) {
    return (
      <div className="compliance-connect-network-profile__comment-log" data-testid="network-profile-comment-log-loading">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="compliance-connect-network-profile__comment-empty" data-testid="network-profile-comment-log-error">
        Unable to load comments.
      </p>
    );
  }

  return (
    <div className="compliance-connect-network-profile__comment-log" data-testid="network-profile-comment-log">
      <NetworkProfileCommentSection
        comments={newComments}
        emptyMessage="No new comments."
        onCommentViewed={handleCommentViewed}
        testId="network-profile-comment-log__new-comments"
        title="New Comments"
      />
      <NetworkProfileCommentSection
        comments={history}
        emptyMessage="No comment history."
        testId="network-profile-comment-log__comment-history"
        title="Comment History"
      />
    </div>
  );
};
